import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';

import { useCustomToast } from '../../Contexts/ToastContext';
import { useUpdatePromo, useDeletePromo, useShowPromoNow } from '../../../queries/promos';
import { ar, arT, promoRuntimeState, stateLineText, promoDesignCount, daypartLabel, DAYPARTS } from './promosLogic';
import ShapeFrame from './kit/ShapeFrame';
import { evLabel, roundLabel, countLabel } from './kit/timing';
import { useInHub } from '../../../nav/useInHub';

/**
 * عروضي — the list screen. Cards as in the approved prototype: real first-design
 * thumbnail inside its shape frame, state badge (derived from the EXISTING
 * runtime state — no new states), shape tag, summary, تعديل, تشغيل/إيقاف and a
 * more-menu (نسخ، اعرضه الآن، حذف). All actions are the existing ones; this
 * component only presents them.
 */

const BADGE = {
  running: ['on', 'b-on'], queued: ['sched', 'b-sched'], outside_window: ['sched', 'b-sched'],
  paused: ['off', 'b-off'], stopped: ['off', 'b-off'], finished: ['off', 'b-off'],
};
export const badgeFor = (stateKey) => BADGE[stateKey] || BADGE.stopped;

export default function PromoList({ promos, devices, libraryById, libraryReady = true, qrLinks, isLoading, isError, refetch, onBack, onNew, onEdit, onDuplicate }) {
  const { t } = useTranslation();
  const inHub = useInHub();
  const tt = (k, o) => t(`promos.v2_${k}`, o);
  const { showToast } = useCustomToast();
  const updatePromo = useUpdatePromo();
  const deletePromo = useDeletePromo();
  const showNow = useShowPromoNow();

  const [filter, setFilter] = useState('all');
  const [menuId, setMenuId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(id); }, []);

  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuId) return undefined;
    const close = (e) => { if (!menuRef.current || !menuRef.current.contains(e.target)) setMenuId(null); };
    const esc = (e) => { if (e.key === 'Escape') setMenuId(null); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menuId]);

  const rows = useMemo(() => promos.map((p) => {
    const state = promoRuntimeState(p, now, promos);
    const [bk] = badgeFor(state.key);
    return { p, state, bk };
  }), [promos, now]);
  const counts = { all: rows.length, on: rows.filter((r) => r.bk === 'on').length, off: rows.filter((r) => r.bk !== 'on').length };
  const shown = rows.filter((r) => filter === 'all' || (filter === 'on' ? r.bk === 'on' : r.bk !== 'on'));

  const scansFor = (p) => (qrLinks || []).filter((l) => l.promo_id === p.id && !l.is_disabled).reduce((n, l) => n + (l.scans || 0), 0);
  const hasCountedQr = (p) => (qrLinks || []).some((l) => l.promo_id === p.id);

  // The card's picture: the rendered design URL stored with the promo (no library needed), else the media library.
  const thumbOf = (p) => {
    const first = (p.media && p.media[0]) || null;
    const id = first?.id || (p.media_ids || [])[0];
    return first?.creative_url || p.template?.items?.[0]?.image || (id ? libraryById.get(id)?.path : null) || null;
  };

  const summary = (p) => {
    const parts = [];
    const n = promoDesignCount(p);
    parts.push(countLabel(t, 'designs', n));
    parts.push(tt('every', { v: evLabel(t, p.interval_seconds) }));
    if (p.mode === 'manual') parts.push(`${tt('manual_only')}${p.manual_round_count ? ` • ${roundLabel(t, p.manual_round_count)}` : ''}`);
    else {
      const dp = DAYPARTS.find((x) => x.k !== 'custom' && x.k !== 'man' && x.from === p.start_time && x.to === p.end_time);
      parts.push(dp ? daypartLabel(t, dp.k) : `${arT(p.start_time)}–${arT(p.end_time)}`);
    }
    const all = !p.device_ids || p.device_ids.length === devices.length;
    parts.push(all ? t('promos.all_devices') : countLabel(t, 'screens', p.device_ids.length));
    return parts.join(' • '); // not '·': next to Arabic-Indic digits a middle dot reads as another zero (٥ · → ٥٠)
  };

  /* ---- existing actions, unchanged semantics ---- */
  const toggleActive = (p) => {
    setTogglingId(p.id);
    updatePromo.mutate({ id: p.id, is_active: !p.is_active }, {
      onSuccess: () => {
        const msg = p.mode === 'manual'
          ? (p.is_active ? t('promos.toast_stopped') : t('promos.toast_started'))
          : (p.is_active ? t('promos.toast_disabled') : t('promos.toast_enabled'));
        showToast('success', t('promos.title'), msg);
      },
      onSettled: () => setTogglingId(null),
    });
  };
  const restartPromo = (p) => {
    setTogglingId(p.id);
    updatePromo.mutate({ id: p.id, is_active: false }, {
      onSuccess: () => updatePromo.mutate({ id: p.id, is_active: true }, {
        onSuccess: () => showToast('success', t('promos.title'), t('promos.toast_restarted')),
        onSettled: () => setTogglingId(null),
      }),
      onError: () => setTogglingId(null),
    });
  };
  const fireNow = (p) => {
    const firstMedia = (p.media && p.media[0]) || null;
    const mediaId = firstMedia?.id || (p.media_ids || [])[0];
    if (!mediaId) return;
    const targetIds = p.device_ids && p.device_ids.length ? p.device_ids : devices.map((d) => d.id);
    if (!targetIds.length) { showToast('error', t('promos.title'), t('promos.no_devices_to_fire')); return; }
    showNow.mutate({ mediaId, deviceIds: targetIds, shape: p.shape, duration: p.show_duration_seconds }, {
      onSuccess: ({ skipped, failed, total }) => {
        if (failed === total) showToast('error', t('promos.title'), t('promos.toast_fire_failed'));
        else if (skipped) showToast('warn', t('promos.title'), t('promos.toast_fire_partial', { skipped, total }));
        else showToast('success', t('promos.title'), t('promos.toast_fire_sent'));
      },
    });
  };
  const remove = (p) => confirmDialog({
    message: t('promos.confirm_delete', { name: p.name }), header: t('promos.confirm_delete_title'),
    acceptClassName: 'p-button-danger', acceptLabel: t('promos.delete'), rejectLabel: t('promos.cancel'),
    accept: () => deletePromo.mutate(p.id),
  });

  return (
    <section className="view-list" aria-label={tt('list_aria')}>
      <div className="lhead">
        <div className="lhead-t">
          {onBack && !inHub && <button className="btn ghost back" type="button" onClick={onBack} aria-label={tt('back_list')}>→</button>}
          {!inHub && (
          <div>
            <h1>{t('promos.title')}</h1>
            <p>{tt('list_sub')}</p>
          </div>
          )}
        </div>
        <button className="btn primary" type="button" onClick={onNew}>+ {tt('new_btn')}</button>
      </div>

      {isError && (
        <div className="note" role="alert">{tt('load_err')} <button className="btn ghost inline" type="button" onClick={() => refetch()}>{tt('retry')}</button></div>
      )}

      {rows.length > 0 && (
        <div className="filters" role="group" aria-label={tt('filter_aria')}>
          {[['all', tt('f_all')], ['on', tt('f_on')], ['off', tt('f_off')]].map(([k, label]) => (
            <button key={k} className="flt" type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{label} <span className="cnt">{ar(counts[k])}</span></button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid">{[0, 1, 2].map((i) => <Skeleton key={i} height="14rem" borderRadius="16px" />)}</div>
      ) : rows.length === 0 ? (
        <div className="empty">
          <b>{tt('empty_t')}</b>
          <span>{tt('empty_s')}</span>
          <button className="btn primary" type="button" onClick={onNew}>+ {tt('new_btn')}</button>
        </div>
      ) : (
        <div className="grid">
          {shown.map(({ p, state, bk }) => {
            const [, cls] = badgeFor(state.key);
            const line = stateLineText(t, p, state, now);
            const finished = p.mode === 'manual' && state.key === 'finished';
            const toggling = togglingId === p.id;
            return (
              <article className="card" key={p.id}>
                <div className="thumb">
                  {thumbOf(p)
                    ? <ShapeFrame shape={p.shape} src={thumbOf(p)} ariaLabel={p.name} />
                    : <div className={`nothumb${libraryReady ? '' : ' loading'}`} role="img" aria-label={p.name}>{libraryReady ? tt('not_rendered') : null}</div>}
                </div>
                <div className="cbody">
                  <div className="crow"><span className="cname">{p.name}</span><span className={`badge ${cls}`}>{tt(`st_${bk}`)}</span></div>
                  <div className="crow"><span className="shape-tag">{tt(`shape_${p.shape}_short`)}</span><span className="cmeta">{summary(p)}</span></div>
                  {line && <div className="cmeta">{line}</div>}
                  {hasCountedQr(p) && <div className="cmeta scans">{tt('scans', { n: ar(scansFor(p)) })}</div>}
                </div>
                <div className="cfoot">
                  <button className="btn" type="button" onClick={() => onEdit(p)}>{t('promos.edit')}</button>
                  {finished
                    ? <button className="btn" type="button" disabled={toggling} onClick={() => restartPromo(p)}>{t('promos.restart')}</button>
                    : <button className="btn" type="button" disabled={toggling} onClick={() => toggleActive(p)}>{p.is_active ? t('promos.stop') : t('promos.start')}</button>}
                  <span className="grow" />
                  <div className="more" ref={menuId === p.id ? menuRef : null}>
                    <button className="btn ghost" type="button" aria-haspopup="menu" aria-expanded={menuId === p.id} aria-label={tt('more')}
                      onClick={() => setMenuId(menuId === p.id ? null : p.id)}>•••</button>
                    {menuId === p.id && (
                      <div className="menu" role="menu">
                        <button role="menuitem" type="button" onClick={() => { setMenuId(null); fireNow(p); }}>{t('promos.fire_now')}</button>
                        <button role="menuitem" type="button" onClick={() => { setMenuId(null); onDuplicate(p); }}>{t('promos.duplicate')}</button>
                        <button role="menuitem" type="button" className="danger" onClick={() => { setMenuId(null); remove(p); }}>{t('promos.delete')}</button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
